import { create } from 'zustand';
import type {
  AttemptState,
  CriteriaScores,
  SSEEvent,
  ValidationDecision,
  ValidationState,
} from '../types';

interface ValidationStoreState extends ValidationState {
  // Mutations
  openDrawer: () => void;
  closeDrawer: () => void;
  reset: () => void;
  dispatch: (event: SSEEvent) => void;
}

const emptyAttempt = (attemptNumber: number): AttemptState => ({
  attemptNumber,
  generatorPrompt: null,
  generatorResponse: null,
  validatorPrompt: null,
  scratchpad: null,
  scores: null,
  weightedScore: null,
  decision: null,
  guidedFeedback: null,
  isGenerating: false,
  isValidating: false,
});

const initialState: ValidationState = {
  runId: null,
  status: 'idle',
  currentAttempt: 0,
  maxAttempts: 3,
  attempts: [],
  finalDecision: null,
  finalScore: null,
  qualityWarning: null,
  drawerOpen: false,
  contentType: null,
};

/**
 * updateAttempt returns a new attempts array with the given attempt patched.
 * If the attempt does not exist yet, it is appended.
 */
function updateAttempt(
  attempts: AttemptState[],
  attemptNumber: number,
  patch: Partial<AttemptState>
): AttemptState[] {
  const idx = attempts.findIndex((a) => a.attemptNumber === attemptNumber);
  if (idx === -1) {
    return [...attempts, { ...emptyAttempt(attemptNumber), ...patch }];
  }
  const updated = [...attempts];
  updated[idx] = { ...updated[idx], ...patch };
  return updated;
}

export const useValidationStore = create<ValidationStoreState>((set) => ({
  ...initialState,

  openDrawer: () => set({ drawerOpen: true }),
  closeDrawer: () => set({ drawerOpen: false }),

  reset: () => set(initialState),

  dispatch: (event: SSEEvent) => {
    set((state) => {
      switch (event.type) {
        case 'attempt_started': {
          const { attempt, maxAttempts } = event.data;
          return {
            status: 'running' as const,
            currentAttempt: attempt,
            maxAttempts,
            attempts: updateAttempt(state.attempts, attempt, {
              attemptNumber: attempt,
              isGenerating: true,
            }),
          };
        }

        case 'generator_prompt': {
          return {
            attempts: updateAttempt(state.attempts, state.currentAttempt, {
              generatorPrompt: {
                systemPrompt: event.data.systemPrompt,
                userPrompt: event.data.userPrompt,
              },
            }),
          };
        }

        case 'generator_response': {
          return {
            attempts: updateAttempt(state.attempts, state.currentAttempt, {
              generatorResponse: event.data.content,
              isGenerating: false,
              isValidating: true,
            }),
          };
        }

        case 'validator_started': {
          return {
            attempts: updateAttempt(state.attempts, state.currentAttempt, {
              isValidating: true,
            }),
          };
        }

        case 'validator_prompt': {
          return {
            attempts: updateAttempt(state.attempts, state.currentAttempt, {
              validatorPrompt: {
                systemPrompt: event.data.systemPrompt,
                userPrompt: event.data.userPrompt,
              },
            }),
          };
        }

        case 'validator_scratchpad': {
          return {
            attempts: updateAttempt(state.attempts, state.currentAttempt, {
              scratchpad: event.data.reasoning,
            }),
          };
        }

        case 'validator_scores': {
          return {
            attempts: updateAttempt(state.attempts, state.currentAttempt, {
              scores: event.data.criteria as CriteriaScores,
              weightedScore: event.data.weightedScore,
            }),
          };
        }

        case 'validator_decision': {
          return {
            attempts: updateAttempt(state.attempts, state.currentAttempt, {
              decision: event.data.decision as ValidationDecision,
              isValidating: false,
            }),
          };
        }

        case 'retry_triggered': {
          const { nextAttempt, guidedFeedback } = event.data;
          // Attach guided feedback to the attempt that just failed
          const attemptsWithFeedback = updateAttempt(
            state.attempts,
            state.currentAttempt,
            { guidedFeedback }
          );
          return {
            attempts: attemptsWithFeedback,
            currentAttempt: nextAttempt,
          };
        }

        case 'complete': {
          return {
            status: 'complete' as const,
            finalDecision: event.data.finalDecision as ValidationDecision,
            finalScore: event.data.weightedScore,
            attempts: updateAttempt(state.attempts, state.currentAttempt, {
              isGenerating: false,
              isValidating: false,
            }),
          };
        }

        case 'quality_warning': {
          return {
            qualityWarning: event.data.message,
          };
        }

        case 'sse_error': {
          return {
            status: 'error' as const,
          };
        }

        default:
          return {};
      }
    });
  },
}));
